/**
 * The sample pull request the demo reviews.
 *
 * ⚠️ DEMO ONLY, AND SAID SO ON THE PAGE. `acme/invoicing` does not exist. The trees below are the
 * repository at three moments — `main`, the pull request as first pushed, and after the author's
 * follow-up push — and the model answers are recorded, because the demo calls no model. EVERYTHING
 * ELSE IS SIBYL ITSELF: the diff, the anchor set, the six gate components, the refuter's citation
 * check, the fingerprints, the triage store, the locks and the posted review are computed by the
 * same code `sibyl review`, `sibyl recheck`, `sibyl triage` and `sibyl post` run.
 */

export const REPO = "acme/invoicing"
export const PR_NUMBER = 42
export const PR_TITLE = "Approve invoices from the list, and import line items in bulk"
export const AUTHOR = "jordan-p"

/** Commit ids. 40 hex, made up, stable so fingerprints and links are stable. */
export const SHA = {
  base: "3f9a1c7e5d2b8a6f4c0e9d1b7a5c3e8f2d6b4a90",
  head1: "a1c4e7b2d9f0638e5c1a7b4d2e9f8c6a3b5d7e10",
  merge1: "b7e2d4c9a1f6830e2c5b7a9d4e1f6c8b3a2d5e71",
  head2: "c9d1e3f5a7b2846c0e9f1a3b5d7c2e4f6a8b0d12",
  merge2: "d2f4a6c8e0b1937d5f2a4c6e8b0d1f3a5c7e9b34",
} as const

export type Version = "v1" | "v2"

const RULES = [
  "# Engineering rules for acme/invoicing",
  "",
  "These are read by people and by the reviewer.",
  "",
  "- Every action on an invoice checks the same visibility rule as reading it.",
  "- Replacing a set of rows writes the new rows before it removes the old ones.",
  "- Counts that feed billing are computed per organisation, never across all tenants.",
  "- A feature flag that nothing reads is not a feature flag.",
  "",
]

const INVOICES_BASE = [
  'import { db } from "./db"',
  'import { canView } from "./auth/visibility"',
  'import type { Ctx } from "./types"',
  "",
  "export async function getInvoice(ctx: Ctx, id: string) {",
  "  const invoice = await db.invoices.get(id)",
  "  if (!invoice || !canView(ctx.user, invoice)) return null",
  "  return invoice",
  "}",
  "",
  "export async function listInvoices(ctx: Ctx) {",
  "  const all = await db.invoices.byOrg(ctx.orgId)",
  "  return all.filter((invoice) => canView(ctx.user, invoice))",
  "}",
  "",
]

const INVOICES_HEAD = [
  'import { db } from "./db"',
  'import { canView } from "./auth/visibility"',
  'import { requirePermission } from "./auth/permissions"',
  'import { NotFound } from "./errors"',
  'import type { Ctx } from "./types"',
  "",
  "export async function getInvoice(ctx: Ctx, id: string) {",
  "  const invoice = await db.invoices.get(id)",
  "  if (!invoice || !canView(ctx.user, invoice)) return null",
  "  return invoice",
  "}",
  "",
  "export async function listInvoices(ctx: Ctx) {",
  "  const all = await db.invoices.byOrg(ctx.orgId)",
  "  return all.filter((invoice) => canView(ctx.user, invoice))",
  "}",
  "",
  "export async function approveInvoice(ctx: Ctx, id: string) {",
  '  await requirePermission(ctx, "approveInvoices")',
  "  const invoice = await db.invoices.get(id)",
  "  if (!invoice) throw new NotFound(id)",
  '  await db.invoices.update(id, { status: "approved", approvedBy: ctx.user.id })',
  "  await db.audit.record(ctx, \"invoice.approved\", id)",
  "  return invoice",
  "}",
  "",
  "export async function approveMany(ctx: Ctx, ids: string[]) {",
  "  const invoices = await db.invoices.getMany(ids)",
  "  const results = []",
  "  for (const invoice of invoices) {",
  "    results.push(await approveInvoice(ctx, invoice.id))",
  "  }",
  "  return results",
  "}",
  "",
]

/** After the author's push: the invoice is loaded through the visibility check before anyone may act on it. */
const INVOICES_V2 = [
  ...INVOICES_HEAD.slice(0, INVOICES_HEAD.indexOf("export async function approveInvoice(ctx: Ctx, id: string) {")),
  "export async function approveInvoice(ctx: Ctx, id: string) {",
  "  const invoice = await getInvoice(ctx, id)",
  "  if (!invoice) throw new NotFound(id)",
  '  await requirePermission(ctx, "approveInvoices", invoice)',
  '  await db.invoices.update(id, { status: "approved", approvedBy: ctx.user.id })',
  '  await db.audit.record(ctx, "invoice.approved", id)',
  "  return invoice",
  "}",
  ...INVOICES_HEAD.slice(INVOICES_HEAD.indexOf("export async function approveMany(ctx: Ctx, ids: string[]) {") - 1),
]

const IMPORT_BASE = [
  'import { db } from "./db"',
  'import { validate, type LineItemInput } from "./lineItems"',
  "",
  "export async function addLineItem(invoiceId: string, row: LineItemInput) {",
  "  return db.lineItems.insert({ invoiceId, ...validate(row) })",
  "}",
  "",
]

const IMPORT_V1 = [
  'import { db } from "./db"',
  'import { validate, type LineItemInput } from "./lineItems"',
  "",
  "export async function addLineItem(invoiceId: string, row: LineItemInput) {",
  "  return db.lineItems.insert({ invoiceId, ...validate(row) })",
  "}",
  "",
  "/** Replace every line item on an invoice with the rows from an uploaded sheet. */",
  "export async function replaceLineItems(invoiceId: string, rows: LineItemInput[]) {",
  "  const existing = await db.lineItems.byInvoice(invoiceId)",
  "  for (const item of existing) {",
  "    await db.lineItems.delete(item.id)",
  "  }",
  "  for (const row of rows) {",
  "    await db.lineItems.insert({ invoiceId, ...validate(row) })",
  "  }",
  "}",
  "",
]

const IMPORT_V2 = [
  'import { db } from "./db"',
  'import { validate, type LineItemInput } from "./lineItems"',
  "",
  "export async function addLineItem(invoiceId: string, row: LineItemInput) {",
  "  return db.lineItems.insert({ invoiceId, ...validate(row) })",
  "}",
  "",
  "/** Replace every line item on an invoice with the rows from an uploaded sheet. */",
  "export async function replaceLineItems(invoiceId: string, rows: LineItemInput[]) {",
  "  const existing = await db.lineItems.byInvoice(invoiceId)",
  "  const staged = []",
  "  for (const row of rows) {",
  "    staged.push(await db.lineItems.insert({ invoiceId, ...validate(row), pending: true }))",
  "  }",
  "  await db.lineItems.removeMany(existing.map((item) => item.id))",
  "  await db.lineItems.publish(staged)",
  "}",
  "",
]

const FLAGS_HEAD = [
  'import type { Org } from "../types"',
  "",
  "/** Tenants with this flag see the Approve button in the invoice list. */",
  "export const listApprovalEnabled = (org: Org) => org.flags?.listApproval ?? false",
  "",
]

const USAGE_BASE = [
  'import { db } from "./db"',
  "",
  "export async function dailySnapshot(day: string) {",
  "  for (const org of await db.orgs.all()) {",
  "    const count = await db.invoices.countByOrg(org.id)",
  "    await db.usage.insert({ day, orgId: org.id, invoices: count })",
  "  }",
  "}",
  "",
]

const USAGE_V1 = [
  'import { db } from "./db"',
  "",
  "export async function dailySnapshot(day: string) {",
  "  const rows = await db.invoices.scan().take(5000)",
  "  const byOrg = new Map<string, number>()",
  "  for (const row of rows) byOrg.set(row.orgId, (byOrg.get(row.orgId) ?? 0) + 1)",
  "  for (const [orgId, count] of byOrg) {",
  "    await db.usage.insert({ day, orgId, invoices: count })",
  "  }",
  "}",
  "",
]

const USAGE_V2 = [
  'import { db } from "./db"',
  "",
  "export async function dailySnapshot(day: string) {",
  "  for (const org of await db.orgs.all()) {",
  "    const count = await db.invoices.countByOrg(org.id)",
  "    await db.usage.insert({ day, orgId: org.id, invoices: count })",
  "  }",
  "}",
  "",
]

const PASSWORD_BASE = [
  "export const MIN_PASSWORD_LENGTH = 12",
  "",
]

const PASSWORD_HEAD = [
  "// Must match the identity service. A stale value here shows a wrong hint but never admits a weak password.",
  "export const MIN_PASSWORD_LENGTH = 14",
  "",
]

const LIST_BASE = [
  'import { formatMoney } from "../lib/money"',
  'import type { Invoice } from "../types"',
  "",
  "export function InvoiceRow({ invoice }: { invoice: Invoice }) {",
  "  return (",
  "    <tr>",
  "      <td>{invoice.number}</td>",
  "      <td>{formatMoney(invoice.totalCents)}</td>",
  '      <td><a href={invoice.attachmentUrl} download={invoice.fileName}>PDF</a></td>',
  "    </tr>",
  "  )",
  "}",
  "",
]

const LIST_HEAD = [
  'import { formatMoney } from "../lib/money"',
  'import { approveInvoice } from "../invoices"',
  'import type { Invoice } from "../types"',
  "",
  "export function InvoiceRow({ invoice }: { invoice: Invoice }) {",
  "  return (",
  "    <tr>",
  "      <td>{invoice.number}</td>",
  "      <td>{formatMoney(invoice.totalCents)}</td>",
  '      <td><a href={invoice.attachmentUrl} download={invoice.fileName}>PDF</a></td>',
  '      <td><button onClick={() => approveInvoice(ctx, invoice.id)}>Approve</button></td>',
  "    </tr>",
  "  )",
  "}",
  "",
]

const MONEY_BASE = [
  "export function formatMoney(cents: number): string {",
  "  return (cents / 100).toLocaleString(\"en-US\", { style: \"currency\", currency: \"USD\" })",
  "}",
  "",
]

const TOTALS_HEAD = [
  "/** Totals shown on the approval list. */",
  "export function formatTotal(cents: number): string {",
  "  return \"$\" + (cents / 100).toFixed(2)",
  "}",
  "",
]

type Tree = Record<string, readonly string[]>

const BASE_TREE: Tree = {
  "SIBYL.md": RULES,
  "src/invoices.ts": INVOICES_BASE,
  "src/lineItemImport.ts": IMPORT_BASE,
  "src/usageSnapshot.ts": USAGE_BASE,
  "src/lib/password-policy.ts": PASSWORD_BASE,
  "src/components/InvoiceRow.tsx": LIST_BASE,
  "src/lib/money.ts": MONEY_BASE,
}

export const TREES: Record<"base" | Version, Tree> = {
  base: BASE_TREE,
  v1: {
    ...BASE_TREE,
    "src/invoices.ts": INVOICES_HEAD,
    "src/lineItemImport.ts": IMPORT_V1,
    "src/lib/tenant-flags.ts": FLAGS_HEAD,
    "src/usageSnapshot.ts": USAGE_V1,
    "src/lib/password-policy.ts": PASSWORD_HEAD,
    "src/components/InvoiceRow.tsx": LIST_HEAD,
    "src/lib/totals.ts": TOTALS_HEAD,
  },
  v2: {
    ...BASE_TREE,
    "src/invoices.ts": INVOICES_V2,
    "src/lineItemImport.ts": IMPORT_V2,
    "src/lib/tenant-flags.ts": FLAGS_HEAD,
    "src/usageSnapshot.ts": USAGE_V2,
    "src/lib/password-policy.ts": PASSWORD_HEAD,
    "src/components/InvoiceRow.tsx": LIST_HEAD,
    "src/lib/totals.ts": TOTALS_HEAD,
  },
}

// ── What the "model" answered ──────────────────────────────────────────────────────────────────

export interface ScriptedFinding {
  readonly dimension: string
  readonly path: string
  /** The real line the finding is about. Its line number is looked up in the tree, as a model would. */
  readonly line: string
  /** What the model quoted, when it did NOT quote the line byte for byte. */
  readonly misquote?: string
  readonly title: string
  readonly reasoning: string
  readonly consequence: string
  readonly inputs: string
  readonly wrongOutcome: string
  readonly fix: string
  readonly effort: "S" | "M" | "L"
  readonly severity: "high" | "medium" | "low"
  readonly rule?: string
  /** The refuter's answer. Absent means it could not disprove the finding. */
  readonly refuted?: { readonly why: string; readonly path: string; readonly line: string }
}

const SECURITY_APPROVE: ScriptedFinding = {
  dimension: "security",
  path: "src/invoices.ts",
  line: '  await requirePermission(ctx, "approveInvoices")',
  title: "Someone who cannot see a private invoice can still approve it",
  reasoning:
    "getInvoice and listInvoices both gate on canView. approveInvoice gates only on the approve permission, then loads the invoice by id and updates it. Nothing between the permission check and the update consults visibility.",
  consequence:
    "A colleague who holds the approve permission but is not allowed to see a private invoice can approve it. The invoice is invisible to them everywhere in the interface, but the approval goes through under their name and releases the payment.",
  inputs: "A private invoice submitted by Dana; Sam is in the same organisation with the approver role but is not a party to the invoice; Sam calls approveInvoice with its id.",
  wrongOutcome: "The invoice is marked approved by Sam although getInvoice returns nothing for Sam.",
  fix: "Load the invoice first and refuse unless canView(ctx.user, invoice) holds, exactly as getInvoice does, before the permission check passes the call through.",
  effort: "S",
  severity: "high",
  rule: "Every action on an invoice checks the same visibility rule as reading it.",
}

const ADVERSARIAL_REPLACE: ScriptedFinding = {
  dimension: "adversarial",
  path: "src/lineItemImport.ts",
  line: "    await db.lineItems.delete(item.id)",
  title: "One bad row in an upload leaves the invoice with no line items and no way back",
  reasoning:
    "The old rows are deleted one by one before any new row is written, and the inserts are separate awaits with no transaction. validate() throws on a malformed row.",
  consequence:
    "If any uploaded row fails validation, the invoice has already lost every line item it had, and the previous set cannot be recovered.",
  inputs: "An invoice with 12 line items; upload a sheet of 12 rows where row 7 has a negative quantity.",
  wrongOutcome: "The 12 original items are gone, rows 1–6 are written, and the invoice total is wrong with no error shown to the approver.",
  fix: "Write the new rows first (or in one transaction) and remove the old ones only after every new row has been written.",
  effort: "M",
  severity: "high",
  rule: "Replacing a set of rows writes the new rows before it removes the old ones.",
}

const ADVERSARIAL_STAGED: ScriptedFinding = {
  dimension: "adversarial",
  path: "src/lineItemImport.ts",
  line: "  await db.lineItems.removeMany(existing.map((item) => item.id))",
  title: "If publishing fails after the old rows are removed, the invoice shows no line items",
  reasoning:
    "The new rows are now staged first, which fixes the original ordering, but removeMany runs before publish and the two are not in one transaction.",
  consequence: "A failure between removing the old rows and publishing the staged ones still leaves the invoice empty to every reader.",
  inputs: "Upload a valid sheet; the database drops the connection after removeMany returns.",
  wrongOutcome: "The old items are removed and the staged items stay pending, so the invoice shows no line items.",
  fix: "Publish the staged rows and remove the old ones in a single transaction, or publish first and remove after.",
  effort: "S",
  severity: "medium",
}

const QUALITY_FLAG: ScriptedFinding = {
  dimension: "code_quality",
  path: "src/lib/tenant-flags.ts",
  line: "export const listApprovalEnabled = (org: Org) => org.flags?.listApproval ?? false",
  title: "The flag that is meant to hide the Approve button is never read",
  reasoning: "listApprovalEnabled is exported and documented as gating the button, but InvoiceRow renders the button unconditionally and nothing imports the flag.",
  consequence: "Turning the flag off for a tenant changes nothing: support will flip it, see the button still there, and stop trusting the flags panel.",
  inputs: "Disable listApproval for an organisation and open its invoice list.",
  wrongOutcome: "The Approve button is still shown on every row.",
  fix: "Read the flag in InvoiceRow, or delete it so it does not claim a control that does not exist.",
  effort: "S",
  severity: "medium",
  rule: "A feature flag that nothing reads is not a feature flag.",
}

const PERF_SNAPSHOT: ScriptedFinding = {
  dimension: "performance",
  path: "src/usageSnapshot.ts",
  line: "  const rows = await db.invoices.scan().take(5000)",
  title: "Daily usage counts silently stop growing once there are more than five thousand invoices",
  reasoning: "The snapshot used to count per organisation through an index. It now scans the whole table with a cap of 5000, across every tenant, then groups.",
  consequence: "Once the table passes the cap every organisation's count is undercounted, and the usage series that billing reads is wrong from that day on.",
  inputs: "6,000 invoices across all organisations; run dailySnapshot.",
  wrongOutcome: "The counts written add up to 5,000, and the organisations whose rows sort last get zero.",
  fix: "Keep the per-organisation count through the index, as main did.",
  effort: "S",
  severity: "medium",
  rule: "Counts that feed billing are computed per organisation, never across all tenants.",
}

const PERF_LOOP: ScriptedFinding = {
  dimension: "performance",
  path: "src/invoices.ts",
  line: "    results.push(await approveInvoice(ctx, invoice.id))",
  title: "Bulk approval issues one round trip per invoice",
  reasoning: "approveMany awaits approveInvoice in a loop, and approveInvoice re-loads each invoice.",
  consequence: "Approving a page of 50 invoices takes 150 sequential queries.",
  inputs: "Select 50 invoices and approve them together.",
  wrongOutcome: "The request takes several seconds.",
  fix: "Batch the update.",
  effort: "M",
  severity: "medium",
  refuted: {
    why: "approveMany already loads all invoices in one call; the per-item work is the audit record each approval must write separately",
    path: "src/invoices.ts",
    line: "  const invoices = await db.invoices.getMany(ids)",
  },
}

const QUALITY_COMMENT: ScriptedFinding = {
  dimension: "code_quality",
  path: "src/lib/password-policy.ts",
  line: "// Must match the identity service. A stale value here shows a wrong hint but never admits a weak password.",
  title: "The comment above this constant promises a safety it only has in one direction",
  reasoning: "The note says a stale value is harmless because the identity service rejects anything weaker. That holds only when this value is too low.",
  consequence: "If this value is ever higher than the service's, the form refuses passwords the service would accept, and the comment says that cannot happen.",
  inputs: "Identity service minimum 12; this constant 14.",
  wrongOutcome: "A 13-character password is refused by the form with no way through.",
  fix: "Say both directions, or cut it to one line saying the two must match.",
  effort: "S",
  severity: "low",
}

const SECURITY_DOWNLOAD: ScriptedFinding = {
  dimension: "security",
  path: "src/components/InvoiceRow.tsx",
  line: '      <td><a href={invoice.attachmentUrl} download={invoice.fileName}>PDF</a></td>',
  title: "The attachment's file name reaches the download attribute unsanitised",
  reasoning: "fileName comes from the uploader. This line is unchanged by the pull request — it is on main already.",
  consequence: "A file name with path characters or a misleading extension is offered to the browser as-is.",
  inputs: "Upload an attachment named ../../invoice.pdf.exe and download it from the list.",
  wrongOutcome: "The browser is offered the uploader's name verbatim.",
  fix: "Derive the download name from the invoice number.",
  effort: "S",
  severity: "medium",
}

const NECESSITY_TOTALS: ScriptedFinding = {
  dimension: "necessity",
  path: "src/lib/totals.ts",
  line: "export function formatTotal(cents: number): string {",
  misquote: "export function formatTotal(amount: number): string {",
  title: "This helper duplicates formatMoney in src/lib/money.ts",
  reasoning: "Same input, same output, different rounding.",
  consequence: "Two implementations of the same formatting will drift, and one of them will be wrong.",
  inputs: "",
  wrongOutcome: "",
  fix: "Use formatMoney.",
  effort: "S",
  severity: "low",
}

/** What each dimension answered, at each version of the pull request. */
export const MODEL: Record<Version, readonly ScriptedFinding[]> = {
  v1: [SECURITY_APPROVE, ADVERSARIAL_REPLACE, QUALITY_FLAG, PERF_SNAPSHOT, PERF_LOOP, QUALITY_COMMENT, SECURITY_DOWNLOAD, NECESSITY_TOTALS],
  v2: [ADVERSARIAL_STAGED, QUALITY_FLAG, QUALITY_COMMENT, SECURITY_DOWNLOAD, NECESSITY_TOTALS],
}

// ── The pull request as GitHub would describe it ───────────────────────────────────────────────

export interface ChangedFileLike {
  path: string
  status: "added" | "modified" | "removed"
  additions: number
  deletions: number
  patch: string
}

/** A unified diff of two line arrays, as one hunk with full context — what GitHub's `patch` field holds. */
export function patchOf(before: readonly string[], after: readonly string[]): { patch: string; additions: number; deletions: number } {
  const a = before[before.length - 1] === "" ? before.slice(0, -1) : [...before]
  const b = after[after.length - 1] === "" ? after.slice(0, -1) : [...after]
  const n = a.length
  const m = b.length
  const L: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0))
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) L[i]![j] = a[i] === b[j] ? L[i + 1]![j + 1]! + 1 : Math.max(L[i + 1]![j]!, L[i]![j + 1]!)
  const ops: string[] = []
  let i = 0
  let j = 0
  let additions = 0
  let deletions = 0
  while (i < n || j < m) {
    if (i < n && j < m && a[i] === b[j]) {
      ops.push(` ${a[i]}`)
      i++
      j++
    } else if (j < m && (i >= n || L[i]![j + 1]! >= L[i + 1]![j]!)) {
      ops.push(`+${b[j]}`)
      j++
      additions++
    } else {
      ops.push(`-${a[i]}`)
      i++
      deletions++
    }
  }
  const header = `@@ -${n === 0 ? 0 : 1},${n} +${m === 0 ? 0 : 1},${m} @@`
  return { patch: [header, ...ops].join("\n"), additions, deletions }
}

export function changedFiles(version: Version): ChangedFileLike[] {
  const base = TREES.base
  const head = TREES[version]
  const out: ChangedFileLike[] = []
  for (const path of Object.keys(head).sort()) {
    const before = base[path]
    const after = head[path] as readonly string[]
    if (before !== undefined && before.join("\n") === after.join("\n")) continue
    const p = patchOf(before ?? [], after)
    out.push({ path, status: before === undefined ? "added" : "modified", ...p })
  }
  return out
}

/** Paths the follow-up push changed, relative to the first push. */
export function pushedPaths(): string[] {
  return Object.keys(TREES.v2)
    .filter((p) => (TREES.v1[p] ?? []).join("\n") !== (TREES.v2[p] ?? []).join("\n"))
    .sort()
}
